import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon
} from '@mui/icons-material';
import { reportingService } from '../../service/reportingService';

const ExportButtons = ({ 
  reportType, 
  reportData, 
  filters, 
  onExportStart, 
  onExportComplete, 
  onExportError,
  disabled = false 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingType, setExportingType] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadFile = (blob, filename) => {
    // Create a proper blob with correct MIME type
    const properBlob = new Blob([blob], { 
      type: filename.endsWith('.pdf') ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    const url = window.URL.createObjectURL(properBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object after a small delay
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  const handleExport = async (format) => {
    if (!reportData || disabled) return;

    setExporting(true);
    setExportingType(format);
    handleClose();

    if (onExportStart) {
      onExportStart(format);
    }

    try {
      let response;
      let filename;

      // Handle different report types and formats
      switch (reportType) {
        case 'sales':
          if (format === 'excel') {
            response = await reportingService.exportInvoiceSalesReportToExcel(
              filters.dateFrom,
              filters.dateTo,
              filters.locationId,
              filters.invoiceType || 'sales',
              1,
              200
            );
          }
          break;
        case 'dispense':
          if (format === 'excel') {
            response = await reportingService.exportDispenseReportToExcel(
              filters.dateFrom,
              filters.dateTo,
              filters.locationId,
              1,
              200
            );
          }
          break;
        case 'daily-sales':
          if (format === 'excel') {
            response = await reportingService.exportDailySalesReportToExcel(
              filters.date,
              filters.locationId,
              1, // Get all data for export
              200 // Higher limit for export
            );
          } else if (format === 'pdf') {
            response = await reportingService.exportDailySalesReportToPDF(
              filters.date,
              filters.locationId,
              1,
              200
            );
          }
          break;
        
        // Future report types can be added here
        case 'monthly-sales':
        case 'inventory':
        case 'customers':
        case 'individual-customer':
          // Use generic export method for future implementations
          response = await reportingService.exportReport(reportType, format, filters);
          break;
        case 'payment-methods':
          if (format === 'excel') {
            response = await reportingService.exportPaymentMethodsReportToExcel(
              filters.dateFrom,
              filters.dateTo,
              filters.locationId
            );
          }
          break;
        case 'payments-received':
          if (format === 'excel') {
            response = await reportingService.exportPaymentsReceivedReportToExcel(
              filters.dateFrom,
              filters.dateTo,
              filters.locationId,
              1,
              200
            );
          }
          break;
        case 'insurance-received':
          if (format === 'excel') {
            response = await reportingService.exportInsuranceReceivedReportToExcel(
              filters.dateFrom,
              filters.dateTo,
              filters.locationId,
              1,
              200
            );
          }
          break;
        case 'due-eye-exam':
          if (format === 'excel') {
            response = await reportingService.exportDueEyeExamReportToExcel(
              filters.dateFrom,
              filters.dateTo,
              filters.locationId,
              1,
              200
            );
          }
          break;
        case 'rx-due-soon':
          if (format === 'excel') {
            response = await reportingService.exportRxDueSoonReportToExcel(
              filters.dateFrom,
              filters.dateTo,
              filters.locationId,
              filters.rxType || 'glasses',
              1,
              200
            );
          }
          break;
        case 'rx-expired':
          if (format === 'excel') {
            response = await reportingService.exportRxExpiredReportToExcel(
              filters.dateFrom,
              filters.dateTo,
              filters.locationId,
              filters.rxType || 'glasses',
              1,
              200
            );
          }
          break;
        
        default:
          throw new Error(`Export not implemented for report type: ${reportType}`);
      }

      // Extract filename from response headers if available
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        filename = filenameMatch ? filenameMatch[1] : `${reportType}-report.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      } else {
        const timestamp = new Date().toISOString().split('T')[0];
        filename = `${reportType}-report-${timestamp}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      }

      // Download the file
      downloadFile(response.data, filename);

      if (onExportComplete) {
        onExportComplete(format, filename);
      }

    } catch (error) {
      console.error('Export error:', error);
      if (onExportError) {
        onExportError(format, error.message || 'Export failed');
      }
    } finally {
      setExporting(false);
      setExportingType(null);
    }
  };

  const isExportSupported = (format) => {
    // Check if export is supported for this report type and format
    const supportedExports = {
      'sales': ['excel'],
      'dispense': ['excel'],
      'daily-sales': ['excel', 'pdf'],
      'monthly-sales': ['excel', 'pdf'],
      'inventory': ['excel', 'pdf'],
      'customers': ['excel', 'pdf'],
      'payment-methods': ['excel'],
      'payments-received': ['excel'],
      'insurance-received': ['excel'],
      'due-eye-exam': ['excel'],
      'rx-due-soon': ['excel'],
      'rx-expired': ['excel'],
      'individual-customer': ['excel', 'pdf']
    };

    return supportedExports[reportType]?.includes(format) || false;
  };

  if (!reportData || !reportType) {
    return null;
  }

  return (
    <>
      <Tooltip title="Export Report">
        <Button
          variant="outlined"
          startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
          onClick={handleClick}
          disabled={disabled || exporting}
          sx={{ ml: 1 }}
        >
          {exporting ? `Exporting ${exportingType?.toUpperCase()}...` : 'Export'}
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {isExportSupported('pdf') && (
          <MenuItem onClick={() => handleExport('pdf')}>
            <ListItemIcon>
              <PdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export as PDF</ListItemText>
          </MenuItem>
        )}
        
        {isExportSupported('excel') && (
          <MenuItem onClick={() => handleExport('excel')}>
            <ListItemIcon>
              <ExcelIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export as Excel</ListItemText>
          </MenuItem>
        )}

        {!isExportSupported('pdf') && !isExportSupported('excel') && (
          <MenuItem disabled>
            <ListItemText>Export not available for this report</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default ExportButtons;
